import Geocode from 'react-geocode';
import { AddressInfoDto } from '../interfaces/address-info.dto';
import { PlacesAutocompleteOption } from '../interfaces';

enum AddressComponentTypeEnum {
	Country = 'country',
	AdminAreaLevel1 = 'administrative_area_level_1',
	AdminAreaLevel2 = 'administrative_area_level_2',
	AdminAreaLevel3 = 'administrative_area_level_3',
	Locality = 'locality',
	PostalTown = 'postal_town',
}

interface AddressComponent {
	types: AddressComponentTypeEnum[];
	short_name: string;
	long_name: string;
}

interface GeocodingResult {
	address_components: AddressComponent[];
	formatted_address: string;
	geometry: {
		location: {
			lat: number;
			lng: number;
		};
	};
}

export class GoogleGeocodingService {
	public async getAddressInfoFromAddress(address: string, apiKey?: string): Promise<AddressInfoDto> {
		const response = await Geocode.fromAddress(address, apiKey);

		const firstResult: GeocodingResult = response.results[0];

		const addressInfo = this.getAddressInfoFromComponents(firstResult);

		return { ...addressInfo, name: address };
	}

	public async getPlaceAutocompleteFromAddress(address: string, apiKey?: string): Promise<PlacesAutocompleteOption> {
		const response = await Geocode.fromAddress(address, apiKey);

		const firstResult: GeocodingResult = response.results[0];

		const placeAutocomplete = this.getPlaceAutocompleteFromComponents(firstResult);

		return placeAutocomplete;
	}

	public async getAddressInfoFromLatLng(lat: number, lng: number, apiKey?: string): Promise<AddressInfoDto> {
		const response = await Geocode.fromLatLng(lat.toString(), lng.toString(), apiKey);

		const firstResult: GeocodingResult = response.results[0];

		const addressInfo = this.getAddressInfoFromComponents(firstResult);

		return addressInfo;
	}

	public async getPlaceAutocompleteFromLatLng(lat: number, lng: number, apiKey?: string): Promise<PlacesAutocompleteOption> {
		const response = await Geocode.fromLatLng(lat.toString(), lng.toString(), apiKey);

		const firstResult: GeocodingResult = response.results[0];

		const placeAutocomplete = this.getPlaceAutocompleteFromComponents(firstResult);

		return placeAutocomplete;
	}

	private sortAddressComponents(components: AddressComponent[]): AddressComponent[] {
		return (components || []).slice().sort((v1: { types: string[] }, v2: { types: string[] }) => {
			const a = v1.types?.[0]?.toUpperCase();
			const b = v2.types?.[0]?.toUpperCase();

			return a === b ? 0 : a > b ? 1 : -1;
		});
	}

	private findComponentValue(
		components: AddressComponent[],
		type: AddressComponentTypeEnum
	): AddressComponent {
		return components.find((item: AddressComponent) => item.types?.includes(type)) as AddressComponent;
	}

	private getAddressInfoFromComponents(geocodingResult: GeocodingResult): AddressInfoDto {
		const { lat, lng } = geocodingResult?.geometry.location ?? {};

		const address = geocodingResult.formatted_address;

		const addressComponents = this.sortAddressComponents(geocodingResult?.address_components);

		const country = this.findComponentValue(addressComponents, AddressComponentTypeEnum.Country)?.long_name;
		const state = (
			this.findComponentValue(addressComponents, AddressComponentTypeEnum.AdminAreaLevel1) ||
			this.findComponentValue(addressComponents, AddressComponentTypeEnum.AdminAreaLevel2) ||
			this.findComponentValue(addressComponents, AddressComponentTypeEnum.AdminAreaLevel3)
		)?.long_name;
		const city = (
			this.findComponentValue(addressComponents, AddressComponentTypeEnum.Locality) ||
			this.findComponentValue(addressComponents, AddressComponentTypeEnum.PostalTown)
		)?.long_name;

		return { lat, lng, city, state, country, name: address };
	}

	private getPlaceAutocompleteFromComponents(geocodingResult: GeocodingResult): PlacesAutocompleteOption {
		const { lat, lng } = geocodingResult?.geometry.location ?? {};

		const address = geocodingResult.formatted_address;

		const addressComponents = this.sortAddressComponents(geocodingResult?.address_components);

		const country = this.findComponentValue(addressComponents, AddressComponentTypeEnum.Country)?.long_name;
		const state = (
			this.findComponentValue(addressComponents, AddressComponentTypeEnum.AdminAreaLevel1) ||
			this.findComponentValue(addressComponents, AddressComponentTypeEnum.AdminAreaLevel2) ||
			this.findComponentValue(addressComponents, AddressComponentTypeEnum.AdminAreaLevel3)
		)?.long_name;
		const city = (
			this.findComponentValue(addressComponents, AddressComponentTypeEnum.Locality) ||
			this.findComponentValue(addressComponents, AddressComponentTypeEnum.PostalTown)
		)?.long_name;

		return {
			id: `${lat}-${lng}`,
			description: address,
			formattedSuggestion: {
				mainText: city,
				secondaryText: `${state}, ${country}`,
			}
		}
	}
}
